import type Uppy from '@uppy/core';
import React, { Suspense, lazy, useEffect } from 'react';

import { useSciDomain } from 'components';
import { SciThemeToUppyDialogMap } from 'enums';
import { useOrderContext } from 'hooks';
import { eventUtils } from 'utils';

import { ReceiptUploaderPlaceholder } from './ReceiptUploaderPlaceholder';

type Props = {
  open: boolean;
  // Undefined while the uploader is still loading (useOrderReceipts).
  uppy: Uppy | undefined;
  onClose: () => void;
};

// The dashboard and the Uppy styles are their own chunk. It starts loading together with the Uppy instance
// (requestUppy below) and is retried once; a chunk that still fails is reported and the dialog shows the
// reload prompt instead of taking the page down with it.
const loadDashboard = () =>
  import('./UploadReceiptDashboard').catch(
    () => import('./UploadReceiptDashboard'),
  );

const UploadReceiptDashboard = lazy(() =>
  loadDashboard().catch((error: Error) => {
    eventUtils.trackException({
      message: `Receipt upload dialog failed to load: ${error.message}`,
    });
    return {
      default: ({ open, onClose }: Props) => (
        <ReceiptUploaderPlaceholder open={open} failed onClose={onClose} />
      ),
    };
  }),
);

export const DefaultUploadReceiptDialog: React.FC<Props> = ({
  open,
  uppy,
  onClose,
}) => {
  const { data: { theme } = {} } = useSciDomain();
  const modalTheme = theme ? SciThemeToUppyDialogMap[theme] : 'dark';
  const {
    receiptsResult: { requestUppy, uppyStatus },
  } = useOrderContext();

  useEffect(() => {
    requestUppy();
    loadDashboard().catch(() => {});
  }, [requestUppy]);

  if (!uppy) {
    return (
      <ReceiptUploaderPlaceholder
        open={open}
        failed={uppyStatus === 'failed'}
        onClose={onClose}
      />
    );
  }

  return (
    <Suspense
      fallback={
        <ReceiptUploaderPlaceholder
          open={open}
          failed={false}
          onClose={onClose}
        />
      }
    >
      <UploadReceiptDashboard
        open={open}
        uppy={uppy}
        theme={modalTheme}
        onClose={onClose}
      />
    </Suspense>
  );
};
